/**
 * Locale-aware date/time formatting — ONE shared formatter (audit medium).
 *
 * The portal was rendering dates three different ways, sometimes within one
 * panel: raw ISO (`2026-07-13`), bare `toLocaleDateString()` (locale-dependent,
 * ambiguous `dd/mm/yyyy` for an Ontario audience), and formatted `en-CA`. This
 * is the single source of truth: `en-CA`, short spelled month, so every date
 * reads the same and unambiguously (Q11 `Intl` discipline).
 */
export function formatDate(value: string | Date | null | undefined): string {
  if (value == null) return '—';
  // Date-only strings ("YYYY-MM-DD", e.g. a payout cycle or shift date) parse
  // as UTC midnight via `new Date()`, which then renders as the *previous* day
  // for any timezone west of UTC. Pin them to local midnight so the calendar
  // date is preserved (no off-by-one). Full datetimes keep normal tz handling.
  const d =
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(`${value}T00:00:00`)
      : value instanceof Date
        ? value
        : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (value == null) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
